/**
 * Variant Image Filter
 * Handles filtering product images based on selected color variants
 */
class VariantImageFilter {
    constructor() {
      this.initializeEventListeners();
      this.isFiltering = false; // Flag to prevent multiple concurrent filtering operations
    }
  
    initializeEventListeners() {
      // Listen for variant changes on collection pages
      document.addEventListener('click', (event) => {
        const colorSwatch = event.target.closest('.color_swatches li a');
        if (colorSwatch) {
          const colorValue = colorSwatch.getAttribute('data-color-value') || 
                            colorSwatch.getAttribute('title') || 
                            this.getColorFromStyle(colorSwatch.style.backgroundColor);
          
          if (colorValue) {
            const productCard = colorSwatch.closest('.card');
            if (productCard) {
              this.filterProductCardImages(productCard, colorValue);
            }
          }
        }
      });
  
      // Replace variant:changed event with product-form change event
      const productForm = document.querySelector('product-form');
      if (productForm) {
        productForm.addEventListener('change', (event) => {
          // Get the current variant from the form
          const currentVariant = event.target.currentVariant;
          
          if (currentVariant && currentVariant.option1) {
            const color = currentVariant.option1;
            
            const productId = currentVariant.product?.id || 
                             document.querySelector('product-form')?.dataset?.productId;
            
            if (productId) {
              const productElements = document.querySelectorAll(`[data-product-id="${productId}"]`);
              productElements.forEach(element => {
                this.filterProductCardImages(element, color);
              });
            }
            
            // Handle product page gallery filtering
            this.filterProductPageImages(color);
            
            // Direct dot update after variant change
            setTimeout(() => {
              const visibleSlides = Array.from(document.querySelectorAll('.product__media-item:not(.hidden)'));
              if (visibleSlides.length > 0) {
                this.updateDots(visibleSlides);
                this.updateActiveDot(0);
              }
            }, 300);
          }
        });
      }
  
      // Handle initial page load - especially for collection pages
      this.initializeProductCards();
      
      // Initialize product page gallery with current selected variant color
      this.initializeProductGallery();
    }
  
    initializeProductCards() {
      // For each product card, show only the first variant images
      document.querySelectorAll('.card').forEach(card => {
        const firstColorSwatch = card.querySelector('.color_swatches li a');
        if (firstColorSwatch) {
          const colorValue = firstColorSwatch.getAttribute('data-color-value') || 
                            firstColorSwatch.getAttribute('title') || 
                            this.getColorFromStyle(firstColorSwatch.style.backgroundColor);
          
          if (colorValue) {
            this.filterProductCardImages(card, colorValue);
          }
        }
      });
    }
  
    initializeProductGallery() {
      // Get the initial selected color from the variant selector
      const productForm = document.querySelector('product-form');
      if (productForm) {
        const variantSelects = productForm.querySelector('variant-radios, variant-selects');
        if (variantSelects) {
          const selectedOption = variantSelects.querySelector('input[type="radio"]:checked, select option:checked');
          if (selectedOption) {
            const color = selectedOption.value;
            this.filterProductPageImages(color);
          }
        }
      }
    }
  
    getColorFromStyle(style) {
      if (!style) return null;
      
      // Extract color from RGB format
      const rgb = style.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (rgb) {
        // Convert RGB to hex
        const r = parseInt(rgb[1]);
        const g = parseInt(rgb[2]);
        const b = parseInt(rgb[3]);
        return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
      }
      
      return style;
    }
  
    filterProductCardImages(productCard, colorValue) {
      if (!productCard || !colorValue) return;
      
      const normalizedColor = colorValue.toLowerCase().trim();
      const slideshow = productCard.querySelector('.product-slideshow');
      
      if (slideshow) {
        // Get all available colors from this product's swatches
        const colorSwatches = productCard.querySelectorAll('.color_swatches li a');
        const availableColors = Array.from(colorSwatches).map(swatch => {
          const swatchColor = swatch.getAttribute('data-color-value') || 
                             swatch.getAttribute('title') || 
                             this.getColorFromStyle(swatch.style.backgroundColor);
          return swatchColor ? swatchColor.toLowerCase().trim() : '';
        }).filter(Boolean);
        
        const slides = slideshow.querySelectorAll('.product-slideshow__slide');
        let hasVisibleSlide = false;
        
        // Track which slides are visible for this color
        const visibleSlides = [];
        
        slides.forEach(slide => {
          const image = slide.querySelector('img');
          if (image) {
            const alt = (image.alt || '').toLowerCase();
            // Extract the last word from alt text as the color
            const altWords = alt.split(' ');
            const imageColor = altWords.length > 0 ? altWords[altWords.length - 1].trim() : '';
            
            // Show if:
            // 1. The image color matches the selected color
            // 2. OR it's a "model" image or has color "model"
            // 3. OR the image has no color specification
            // 4. BUT hide if the image has a color that's not in available variants
            const isSelectedColor = imageColor === normalizedColor;
            const isModelImage = alt.includes('model') || imageColor === 'model';
            const hasNoColor = !imageColor || imageColor === '';
            const isUnavailableColor = imageColor && !availableColors.includes(imageColor) && imageColor !== 'model';
            
            if (isSelectedColor || isModelImage || (hasNoColor && !isUnavailableColor)) {
              slide.style.display = '';
              hasVisibleSlide = true;
              visibleSlides.push(slide);
            } else {
              slide.style.display = 'none';
            }
          }
        });
        
        // If no slides are visible, show the first one
        if (!hasVisibleSlide && slides.length > 0) {
          slides[0].style.display = '';
          visibleSlides.push(slides[0]);
        }
        
        // Update active class for the first visible slide
        if (visibleSlides.length > 0) {
          slides.forEach(s => s.classList.remove('active'));
          visibleSlides[0].classList.add('active');
        }
        
        // Trigger a custom event that the card product slideshow can listen for
        slideshow.dispatchEvent(new CustomEvent('variantImageFiltered', {
          detail: {
            color: normalizedColor,
            visibleSlides: visibleSlides
          }
        }));
        
        // Add a data attribute to track the current color
        slideshow.setAttribute('data-current-color', normalizedColor);
      }
    }
  
    filterProductPageImages(color) {
      if (!color || this.isFiltering) return;
      
      this.isFiltering = true;
      
      const normalizedColor = color.toLowerCase().trim();
      const productMediaGallery = document.querySelector('.product__media-gallery');
      
      if (!productMediaGallery) {
        this.isFiltering = false;
        return;
      }
      
      // Get all media items and store them in the original DOM order
      const allMediaItems = Array.from(productMediaGallery.querySelectorAll('.product__media-item'));
      
      // First hide all items and reset visible-index attributes
      allMediaItems.forEach(item => {
        item.classList.add('hidden');
        item.removeAttribute('data-visible-index');
      });
      
      // Filter out visible items based on color - keep original DOM order for the filter result
      let visibleItems = [];
      
      // Apply filtering while preserving original DOM order
      allMediaItems.forEach(item => {
        const dataColor = item.getAttribute('data-color')?.toLowerCase() || '';
        const classList = item.classList[0]?.toLowerCase() || '';
        
        // Show if:
        // 1. The image color matches the selected color
        // 2. OR it's a "model" image
        const isSelectedColor = dataColor === normalizedColor;
        const isModelImage = classList.includes('model') || dataColor === 'model';
        
        if (isSelectedColor || isModelImage) {
          item.classList.remove('hidden');
          visibleItems.push(item);
        }
      });
      
      // If no items are visible, show the first one
      if (visibleItems.length === 0 && allMediaItems.length > 0) {
        allMediaItems[0].classList.remove('hidden');
        visibleItems.push(allMediaItems[0]);
      }
      
      // Update mobile slideshow with these properly ordered slides
      this.updateMobileSlideshow(visibleItems);
      
      // Update lightGallery if it exists
      if (window.refreshLightGallery) {
        const gallery = document.querySelector('.lg-gallery');
        if (gallery) {
          window.refreshLightGallery(gallery);
        }
      }
      
      // Directly update the dot navigation after filtering
      setTimeout(() => {
        if (window.variantImageFilter) {
          const visibleSlides = Array.from(document.querySelectorAll('.product__media-item:not(.hidden)'));
          this.updateDots(visibleSlides);
          this.updateActiveDot(0);
        }
      }, 50);
      
      // Force dots update with increasing delays to ensure it happens after all DOM updates
      [50, 150, 300, 500].forEach(delay => {
        setTimeout(() => {
          if (window.variantImageFilter) {
            const currentVisibleSlides = Array.from(document.querySelectorAll('.product__media-item:not(.hidden)'));
            this.updateDots(currentVisibleSlides);
            this.updateActiveDot(0);
          }
        }, delay);
      });
      
      // Update media gallery data attribute
      productMediaGallery.setAttribute('data-current-color', normalizedColor);
      
      // Dispatch event for other components to react
      productMediaGallery.dispatchEvent(new CustomEvent('gallery:filtered', {
        detail: {
          color: normalizedColor,
          visibleItems: visibleItems
        }
      }));
      
      this.isFiltering = false;
    }
    
    updateMobileSlideshow(visibleItems) {
      if (!visibleItems || !visibleItems.length) {
        return;
      }
      
      // Get the mobile slideshow elements
      const mobileSlideshow = document.querySelector('.mobile-slideshow');
      if (!mobileSlideshow) {
        return;
      }
      
      // First reset all active states
      const allSlides = mobileSlideshow.querySelectorAll('.product__media-item');
      allSlides.forEach((slide) => {
        slide.classList.remove('is-active');
      });
      
      // Make the first visible item active
      if (visibleItems[0]) {
        visibleItems[0].classList.add('is-active');
      }
      
      // Assign visible indices sequentially based on their DOM order
      visibleItems.forEach((item, index) => {
        const visibleIndex = index + 1; // 1-based indexing
        item.setAttribute('data-visible-index', visibleIndex.toString());
      });
      
      // Update pagination dots - recreate them completely
      this.updateDots(visibleItems);
      
      // Always set the first dot as active since we reset to first slide
      this.updateActiveDot(0);
    }
    
    updateDots(visibleItems) {
      const dotsContainer = document.querySelector('.slideshow-dots');
      const paginationContainer = document.querySelector('.mobile-slideshow-pagination');
      const controlsContainer = document.querySelector('.mobile-slideshow-controls');
      
      if (!dotsContainer) {
        return;
      }
      
      if (!visibleItems || visibleItems.length <= 1) {
        dotsContainer.innerHTML = '';
        // Hide controls and pagination when only 1 slide
        if (paginationContainer) {
          paginationContainer.style.display = 'none';
        }
        if (controlsContainer) {
          controlsContainer.style.display = 'none';
        }
        return;
      }
      
      // Show controls and pagination when more than 1 slide
      if (paginationContainer) {
        paginationContainer.style.display = '';
      }
      if (controlsContainer) {
        controlsContainer.style.display = '';
      }
      
      // Clear existing dots
      dotsContainer.innerHTML = '';
      
      // Create new dots for each visible item
      visibleItems.forEach((item, index) => {
        const dot = document.createElement('button');
        dot.classList.add('slideshow-dot');
        
        // First slide is always active after filtering
        if (index === 0) {
          dot.classList.add('active');
        }
        
        dot.setAttribute('data-slide-index', (index + 1).toString());
        dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
        
        // Add click event that uses visible index
        dot.addEventListener('click', () => {
          this.goToSlide(document.querySelector('.mobile-slideshow'), index + 1);
        });
        
        dotsContainer.appendChild(dot);
      });
    }
    
    updateActiveDot(activeIndex) {
      const dots = document.querySelectorAll('.slideshow-dots .slideshow-dot');
      
      if (!dots || dots.length === 0) {
        return;
      }
      
      // Ensure activeIndex is within bounds
      if (activeIndex < 0 || activeIndex >= dots.length) {
        activeIndex = 0; // Default to first dot if out of bounds
      }
      
      // Update dot states
      dots.forEach((dot, i) => {
        if (i === activeIndex) {
          dot.classList.add('active');
        } else {
          dot.classList.remove('active');
        }
      });
    }
    
    goToSlide(slideshow, visibleIndex) {
      if (!slideshow) {
        return;
      }
      
      const allSlides = slideshow.querySelectorAll('.product__media-item');
      const visibleSlides = Array.from(slideshow.querySelectorAll('.product__media-item:not(.hidden)'));
      
      if (!visibleSlides.length) {
        return;
      }
      
      // We need to ensure all visible slides have valid visible-index values
      let needToSetVisibleIndexes = visibleSlides.some(slide => 
        !slide.getAttribute('data-visible-index') || 
        slide.getAttribute('data-visible-index') === 'null'
      );
      
      if (needToSetVisibleIndexes) {
        visibleSlides.forEach((slide, index) => {
          const newVisibleIndex = index + 1;
          slide.setAttribute('data-visible-index', newVisibleIndex.toString());
        });
      }
      
      // Sort visible slides by their visible-index attribute
      const sortedVisibleSlides = [...visibleSlides].sort((a, b) => {
        const aIndex = parseInt(a.getAttribute('data-visible-index') || '0');
        const bIndex = parseInt(b.getAttribute('data-visible-index') || '0');
        return aIndex - bIndex;
      });
      
      // Calculate actual index (0-based) and clamp to valid range
      const targetIndex = Math.min(Math.max(visibleIndex - 1, 0), sortedVisibleSlides.length - 1);
      
      // Remove active class from all slides
      visibleSlides.forEach(slide => slide.classList.remove('is-active'));
      
      // Add active class to target slide
      if (sortedVisibleSlides[targetIndex]) {
        sortedVisibleSlides[targetIndex].classList.add('is-active');
      }
      
      // Update dots
      this.updateActiveDot(targetIndex);
    }
  
    containsAnyColorName(alt, productCard) {
      // Try to get color options from the swatches
      const colorSwatches = productCard.querySelectorAll('.color_swatches li a');
      const colors = Array.from(colorSwatches).map(swatch => {
        return swatch.getAttribute('data-color-value') || 
               swatch.getAttribute('title') || 
               this.getColorFromStyle(swatch.style.backgroundColor);
      }).filter(Boolean);
      
      if (colors.length === 0) return false;
      
      // Get the last word from alt as the color
      const altWords = alt.split(' ');
      const lastWord = altWords.length > 0 ? altWords[altWords.length - 1].trim() : '';
      
      // Special case: "model" is not considered a color for filtering purposes
      if (lastWord === 'model') return false;
      
      // Check if the last word in alt text corresponds to an available color
      return colors.some(color => lastWord === color.toLowerCase().trim());
    }
  }
  
  // Initialize the variant image filter
  document.addEventListener('DOMContentLoaded', () => {
    // Remove the variant:changed event debug listener and replace with product-form change listener
    const productForm = document.querySelector('product-form');
    if (productForm) {
      // Replace the variant:changed event listener with product-form change event
      productForm.addEventListener('change', (event) => {
        // Force dot update whenever any variant change event is detected
        setTimeout(() => {
          const visibleSlides = Array.from(document.querySelectorAll('.product__media-item:not(.hidden)'));
          if (visibleSlides.length > 0 && window.variantImageFilter) {
            window.variantImageFilter.updateDots(visibleSlides);
            window.variantImageFilter.updateActiveDot(0);
          }
        }, 300);
      });
    }

    // Create a manual function to update dots that we can call from the developer console
    window.manuallyUpdateDots = () => {
      const visibleSlides = Array.from(document.querySelectorAll('.product__media-item:not(.hidden)'));
      if (visibleSlides.length > 0 && window.variantImageFilter) {
        window.variantImageFilter.updateDots(visibleSlides);
        window.variantImageFilter.updateActiveDot(0);
        return `Updated dots for ${visibleSlides.length} slides`;
      } else {
        return "No visible slides found";
      }
    };

    window.variantImageFilter = new VariantImageFilter();
    
    // Set up event listeners for mobile slideshow navigation
    const setupSlideNavigation = () => {
      const prevButton = document.querySelector('.mobile-slideshow-controls .slider-button--prev');
      const nextButton = document.querySelector('.mobile-slideshow-controls .slider-button--next');
      const slideshow = document.querySelector('.mobile-slideshow');
      
      if (!slideshow) {
        return;
      }
      
      const visibleSlides = Array.from(slideshow.querySelectorAll('.product__media-item:not(.hidden)'));
      
      // Check for missing visible-index values and set them if needed
      let needToSetVisibleIndexes = visibleSlides.some(slide => 
        !slide.getAttribute('data-visible-index') || 
        slide.getAttribute('data-visible-index') === 'null'
      );
      
      if (needToSetVisibleIndexes) {
        visibleSlides.forEach((slide, index) => {
          const newVisibleIndex = index + 1;
          slide.setAttribute('data-visible-index', newVisibleIndex.toString());
        });
      }
      
      // Remove any existing event listeners to prevent duplicates
      if (prevButton) {
        const newPrevButton = prevButton.cloneNode(true);
        prevButton.parentNode.replaceChild(newPrevButton, prevButton);
        
        newPrevButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          
          const visibleSlides = Array.from(
            slideshow.querySelectorAll('.product__media-item:not(.hidden)')
          ).sort((a, b) => {
            const aIndex = parseInt(a.getAttribute('data-visible-index') || '0');
            const bIndex = parseInt(b.getAttribute('data-visible-index') || '0');
            return aIndex - bIndex;
          });
          
          // Don't navigate if only 1 slide
          if (visibleSlides.length <= 1) {
            return;
          }
          
          const currentSlide = slideshow.querySelector('.product__media-item.is-active');
          
          if (!currentSlide) {
            return;
          }
          
          // Find current index based on position in the sorted visibleSlides array
          const currentIndex = visibleSlides.indexOf(currentSlide);
          
          if (currentIndex === -1) {
            window.variantImageFilter.goToSlide(slideshow, 1);
            return;
          }
          
          // Calculate previous index (with wrap-around)
          const prevIndex = (currentIndex - 1 + visibleSlides.length) % visibleSlides.length;
          console.log(`Navigating to previous index: ${prevIndex}`);
          
          // Go to previous slide using 1-based index
          window.variantImageFilter.goToSlide(slideshow, prevIndex + 1);
        });
      }
      
      if (nextButton) {
        const newNextButton = nextButton.cloneNode(true);
        nextButton.parentNode.replaceChild(newNextButton, nextButton);
        
        newNextButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          
          const visibleSlides = Array.from(
            slideshow.querySelectorAll('.product__media-item:not(.hidden)')
          ).sort((a, b) => {
            const aIndex = parseInt(a.getAttribute('data-visible-index') || '0');
            const bIndex = parseInt(b.getAttribute('data-visible-index') || '0');
            return aIndex - bIndex;
          });
          
          // Don't navigate if only 1 slide
          if (visibleSlides.length <= 1) {
            return;
          }
          
          const currentSlide = slideshow.querySelector('.product__media-item.is-active');
          
          if (!currentSlide) {
            return;
          }
          
          // Find current index based on the current slide's position in sorted visible slides array
          const currentIndex = visibleSlides.indexOf(currentSlide);
          
          if (currentIndex === -1) {
            window.variantImageFilter.goToSlide(slideshow, 1);
            return;
          }
          
          // Calculate next index (with wrap-around)
          const nextIndex = (currentIndex + 1) % visibleSlides.length;
          
          // Go to next slide using 1-based index
          window.variantImageFilter.goToSlide(slideshow, nextIndex + 1);
        });
      }
    };
    
    // Initialize the navigation immediately
    setupSlideNavigation();
    
    // Also listen for variant changes directly to reinitialize navigation and fix dots
    if (productForm) {
      productForm.addEventListener('change', (event) => {
        const currentVariant = event.target.currentVariant;
        
        if (currentVariant && currentVariant.option1) {
          // Wait for filtering and DOM updates to complete with multiple retry attempts
          [100, 200, 300, 500].forEach(delay => {
            setTimeout(() => {
              const slideshow = document.querySelector('.mobile-slideshow');
              if (slideshow) {
                const visibleSlides = Array.from(slideshow.querySelectorAll('.product__media-item:not(.hidden)'));
                
                if (visibleSlides.length > 0) {
                  visibleSlides.forEach((slide, index) => {
                    slide.setAttribute('data-visible-index', (index + 1).toString());
                  });
                  
                  visibleSlides.forEach(slide => slide.classList.remove('is-active'));
                  visibleSlides[0].classList.add('is-active');
                  
                  if (window.variantImageFilter) {
                    window.variantImageFilter.updateDots(visibleSlides);
                    window.variantImageFilter.updateActiveDot(0);
                  }
                  
                  setupSlideNavigation();
                }
              }
            }, delay);
          });
        }
      });
    }
    
    // Also reinitialize after filtering
    document.addEventListener('gallery:filtered', () => {
      setTimeout(setupSlideNavigation, 100);
    });
  });
  
  // Export for module usage
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = VariantImageFilter;
  }
